import type { AnalyticsIdentity, OgEvent, ShellBridgeLike } from '@opengov/analytics';
import { usePspOptions } from '@opengov/app-core/psp/context/psp.context';
import type { ISessionV3 } from '@opengov/app-core/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OG_ANALYTICS_SOURCE_APP, type ReportingOgAnalytics, useOgAnalytics } from './ogAnalyticsClient';

/**
 * Session analytics, copied from `@opengov/bnp-core`'s session analytics (3.25.0) instead of
 * upgrading reporting-app's bnp-core from 3.10.0. The event names and properties match bnp-core's,
 * so reporting's `signed_in` and `product_session_started` join the same series B&P and PSP already
 * send. Swap this file for the bnp-core exports (`useTrackSignedIn`, `sessionAnalyticsEventSpec`)
 * once reporting-app upgrades.
 *
 * `signed_out` is not sent: reporting-app has no sign-out control of its own (standalone uses the
 * `@opengov/app-core` navigation), and mounted in PSP the shell sends it.
 */

const SIGNED_IN: (typeof OgEvent)['SignedIn'] = 'signed_in';
/** `ogEvent('product_session', OgAction.Started)` — no shared `OgEvent` covers it. */
const PRODUCT_SESSION_STARTED = 'product_session_started';

/** The app reporting's session events are attributed to, sent as `app_key` like B&P's. */
export const OG_ANALYTICS_APP_KEY = 'reporting';

export const sessionAnalyticsEventNames = {
  SignedIn: SIGNED_IN,
  ProductSessionStarted: PRODUCT_SESSION_STARTED
} as const;

/** Spread into `defineEvents({ ... })` so the SDK validates the event. Same shape as bnp-core's. */
export const sessionAnalyticsEventSpec = {
  [SIGNED_IN]: { method: 'string', app_key: 'string', source_app: 'string' },
  [PRODUCT_SESSION_STARTED]: { app_key: 'string', source_app: 'string' }
} as const;

/** Which app the user is in, and which host app sent the event. */
export type SessionEventContext = { app_key: string; source_app: string };

/**
 * Users rarely pass through a real Auth0 login here (they arrive with a session from another app),
 * so "signed in" means a signed-in user loaded the app.
 *
 * Standalone only: in PSP or an iframe the shell owns the session and sends its own. Safe before
 * identity lands — with `requireIdentity` the client holds the event and back-fills it.
 *
 * Returns whether it sent, so a caller can tell a real send from a skipped one.
 */
export const trackSignedIn = (client: ReportingOgAnalytics, context: SessionEventContext): boolean => {
  if (client.mode !== 'standalone') {
    return false;
  }
  client.track(SIGNED_IN, { method: 'auth0', ...context });
  return true;
};

/** Modes that deliver: the app's own client, or a module's client forwarding over the shell bridge. */
const PRODUCT_SESSION_MODES: ReadonlySet<string> = new Set(['standalone', 'shell']);

/**
 * Marks a signed-in user loading the product. Unlike `signed_in`, this is sent in PSP too: the shell
 * knows the user signed in to PSP, not that they opened Reporting, so only the module can say so.
 * Mounted in PSP the client forwards it over the bridge to the shell's client; it is never sent twice.
 *
 * Returns whether it sent, so a caller can tell a real send from a skipped one.
 */
export const trackProductSessionStarted = (client: ReportingOgAnalytics, context: SessionEventContext): boolean => {
  if (!PRODUCT_SESSION_MODES.has(client.mode)) {
    return false;
  }
  client.track(PRODUCT_SESSION_STARTED, { ...context });
  return true;
};

type SessionEventHookParams = {
  /** `undefined` sends nothing — e.g. before the session has loaded. */
  client: ReportingOgAnalytics | undefined;
  userUuid: string;
  appKey: string;
  source: string;
};

type SendSessionEvent = (client: ReportingOgAnalytics, context: SessionEventContext) => boolean;

/**
 * Calls `send` once per load of the calling component. A refresh or new tab sends again; an `appKey`
 * change within the same load does not. Nothing is written to browser storage.
 *
 * Keyed on client + user, so the real client replacing the no-op one, or a different user, still
 * sends. `send` reports whether it sent, so a skipped send is retried when the client changes.
 */
function useTrackOncePerLoad({ client, userUuid, appKey, source }: SessionEventHookParams, send: SendSessionEvent) {
  const context = useMemo(() => ({ app_key: appKey, source_app: source }), [appKey, source]);
  const sentFor = useRef<{ client: ReportingOgAnalytics; userUuid: string } | null>(null);

  useEffect(() => {
    if (!client) {
      return;
    }
    const alreadySent = sentFor.current?.client === client && sentFor.current.userUuid === userUuid;
    if (alreadySent) {
      return;
    }
    if (send(client, context)) {
      sentFor.current = { client, userUuid };
    }
  }, [client, userUuid, context, send]);
}

/**
 * Sends `signed_in` once per load (standalone only — see `trackSignedIn`), and
 * `product_session_started` on the same terms, standalone and in PSP alike — see
 * `trackProductSessionStarted`.
 */
export function useTrackSignedIn(params: SessionEventHookParams): void {
  useTrackOncePerLoad(params, trackSignedIn);
  useTrackOncePerLoad(params, trackProductSessionStarted);
}

const hasSignedInVisitor = (identity: AnalyticsIdentity): boolean => !!identity.visitorId;

/**
 * Whether PSP's analytics client is ready to receive a module's events. Always `true` outside PSP.
 *
 * Mounted in PSP, events go through `shell.analytics`, which **drops** `track()` until PSP's own
 * client exists — PSP creates it only once its `psp-og-analytics` flag resolves, which can be after
 * reporting has mounted. The bridge's identity is empty until then, and identity-change handlers are
 * held and attached when the client is created, so a signed-in visitor on the bridge means PSP is
 * recording. `@opengov/psp-contract` doesn't declare `shell.analytics`, so it is read structurally.
 */
export function useIsShellAnalyticsReady(): boolean {
  const pspOptions = usePspOptions();
  const shellAnalytics = (pspOptions?.shell as unknown as ShellBridgeLike | undefined)?.analytics;
  const [hasShellVisitor, setHasShellVisitor] = useState(() =>
    shellAnalytics ? hasSignedInVisitor(shellAnalytics.getIdentity()) : false
  );

  useEffect(() => {
    if (!shellAnalytics || hasShellVisitor) {
      return undefined;
    }
    const markReadyOnVisitor = (identity: AnalyticsIdentity) => {
      if (hasSignedInVisitor(identity)) {
        setHasShellVisitor(true);
      }
    };
    markReadyOnVisitor(shellAnalytics.getIdentity());
    return shellAnalytics.onIdentityChange(markReadyOnVisitor);
  }, [shellAnalytics, hasShellVisitor]);

  return !pspOptions || hasShellVisitor;
}

/**
 * Sends reporting-app's `signed_in` and `product_session_started` once the session has loaded and,
 * mounted in PSP, once PSP's analytics client is ready to receive them (`useIsShellAnalyticsReady`).
 */
export function useOgSignedInTracking({ session }: { session?: ISessionV3 }): void {
  const analytics = useOgAnalytics();
  const isShellAnalyticsReady = useIsShellAnalyticsReady();
  const userUuid = session?.uuid;
  const canSend = !!userUuid && isShellAnalyticsReady;

  useTrackSignedIn({
    client: canSend ? analytics : undefined,
    userUuid: userUuid ?? '',
    appKey: OG_ANALYTICS_APP_KEY,
    source: OG_ANALYTICS_SOURCE_APP
  });
}
